import { SelectionModel } from '@angular/cdk/collections';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { v4 as uuidv4 } from 'uuid';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomOverlayContainer, CUSTOM_OVERLAY_CONFIG } from '../../services/custom-overlay-container';
import { SelectInputComponent } from '../select-input/select-input.component';

@Component({
  selector: 'app-filter-select-input',
  templateUrl: './filter-select-input.component.html',
  styleUrls: ['./filter-select-input.component.scss'],
  standalone: false,
  providers: [
    Overlay,
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    { provide: CUSTOM_OVERLAY_CONFIG, useValue: { overlayContainerClass: 'filter-select-overlay-container' } }
  ]
})
export class FilterSelectInputComponent<T> extends SelectInputComponent<T> implements OnInit, OnDestroy, OnChanges {
  /**
   * The input's ID attribute.
   */
  @Input()
  public inputId = `input-${uuidv4()}`;

  @Input()
  public emptyMessage = 'No options found';

  @ViewChild(MatMenuTrigger, { static: true }) public trigger: MatMenuTrigger;

  @ViewChild('selectInput', { static: true }) public selectInput: ElementRef<HTMLInputElement>;

  @ViewChild(MatSelectionList, { static: true }) public selectionList: MatSelectionList;

  public prevItem: T | null = null;
  public currItem: T | null = null;
  public filteredItems: T[] = [];

  public selection = new SelectionModel(false);

  private readonly destroyed = new Subject<void>();

  public readonly filterForm = new FormGroup({
    filter: new FormControl(''),
  });
  public get filter() { return this.filterForm.controls.filter; }

  public get isMenuOpened(): boolean {
    return this.trigger.menuOpen;
  }

  public ngOnInit(): void {
    this.setUpSelectSubscription();
    this.setUpFilterSubscription();
    this.setUpClickSubscription();
  }

  public ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if(changes['values']) {
      this.prevItem = null;
      this.currItem = null;
      this.filter.setValue('');
    }
  }

  public onMenuOpen(): void {
    this.prevItem = this.currItem;
    this.currItem = null;
    this.filter.setValue('');
  }

  public onMenuClose(): void {
    this.selectInput.nativeElement.blur();
    if (!this.currItem) {
      this.currItem = this.prevItem;
      this.prevItem = null;
      this.filter.setValue(this.currItem ? this.display(this.currItem) : '');
    }
  }

  public updateFilter(filter: string): void {
    const normalised = (value: string) => value?.trim().toLowerCase();
    const query = normalised(filter);
    if (!query) {
      this.filteredItems = this.values ?? [];
    } else {
      this.filteredItems = this.values?.filter(item => normalised(this.display(item)).includes(query)) ?? [];
    }
  }

  public selectItem(item: T): void {
    // necessary to track selected item in SelectionModel as virtual scroll discards state of items in selection list
    this.selection.select(item);
    this.filter.setValue(this.display(item));
    this.control.setValue(this.value(item));
    this.currItem = item;
    this.prevItem = null;
  }

  public clear(): void {
    this.selection.clear();
    this.filter.setValue('');
    this.control?.setValue(null);
    this.currItem = null;
    this.prevItem = null;
  }

  private setUpSelectSubscription(): void {
    this.selectionList.selectionChange
      .pipe(takeUntil(this.destroyed))
      .subscribe((change: MatSelectionListChange) => {
        const item = change.options[0].value;
        this.selectItem(item);
      });
  }

  private setUpFilterSubscription(): void {
    this.filter.valueChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe((value: string) => {
        this.updateFilter(value);
      });
  }

  private setUpClickSubscription(): void {
    // stop the menu closing if the input is clicked
    fromEvent(this.selectInput.nativeElement, 'click', { capture: true })
      .pipe(takeUntil(this.destroyed))
      .subscribe((event: Event) => {
        if (this.isMenuOpened) {
          event.stopPropagation();
        }
      });

    // close menu if clicked outside
    fromEvent(document, 'click')
      .pipe(takeUntil(this.destroyed))
      .subscribe((event: Event) => {
        const inputElem = this.selectInput.nativeElement.parentElement;
        const inputClicked = inputElem?.contains(event.target as Node);

        if (this.isMenuOpened && !inputClicked) {
          this.trigger.closeMenu();
        }
      });
  }
}
